import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../../components/ui/Button';
import { Skeleton } from 'primereact/skeleton';

import { useGetPlaylists } from '../../../queries/wallPlaylistLibrary';
import { useCreateAssignment, useDeleteAssignment } from '../../../queries/wallAssignments';

/**
 * Swap which Content Library playlist a wall — OR a whole wall group — plays,
 * directly from its page without going to the Library. Picking a playlist
 * creates/updates the target's single always-on assignment (the backend upserts by
 * wall_id|group_id, so this never stacks duplicates); the "own/none" row removes the
 * assignment. For a wall that falls back to the wall-bound playlist; for a group the
 * members fall back to their own resolution. Resolves on the next Start/Refresh —
 * never writes RTDB.
 *
 * Pass EITHER wallId or groupId.
 */
const OWN = '__own__';

export default function ChangePlaylistDialog({ visible, onHide, wallId, groupId, currentPlaylistId, assignmentId }) {
  const { t } = useTranslation();
  const { data: playlists = [], isLoading } = useGetPlaylists();
  const assign = useCreateAssignment();
  const unassign = useDeleteAssignment();
  const [selected, setSelected] = useState(null); // playlist id | OWN
  const isGroup = !!groupId;

  React.useEffect(() => {
    if (visible) setSelected(currentPlaylistId || null);
  }, [visible, currentPlaylistId]);

  const saving = assign.isLoading || unassign.isLoading;
  const unchanged = !selected || selected === currentPlaylistId;

  const apply = async () => {
    try {
      if (selected === OWN) {
        if (assignmentId) await unassign.mutateAsync(assignmentId);
      } else if (selected && selected !== currentPlaylistId) {
        await assign.mutateAsync(isGroup
          ? { group_id: groupId, playlist_id: selected }
          : { wall_id: wallId, playlist_id: selected });
      }
      onHide();
    } catch { /* toast handled in hook */ }
  };

  const rowStyle = (active) => ({
    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', cursor: 'pointer',
    borderRadius: 10, border: `1px solid ${active ? 'var(--pp-btn-honey)' : 'var(--pp-border)'}`,
    background: active ? 'color-mix(in srgb, var(--pp-btn-honey) 10%, transparent)' : 'var(--pp-bg-secondary)',
  });

  return (
    <Dialog header={t('video_wall.change_playlist')} visible={visible} onHide={onHide}
      style={{ width: 'min(95vw, 560px)' }} modal>
      <p className="small mb-3" style={{ color: 'var(--pp-text-secondary)' }}>
        {isGroup ? t('video_wall.groups.change_playlist_hint') : t('video_wall.change_playlist_hint')}
      </p>

      {isLoading ? (
        <div className="d-flex flex-column gap-2">{[...Array(3)].map((_, i) => <Skeleton key={i} height="3rem" />)}</div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {playlists.length === 0 && (
            <p className="small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.no_library_playlists')}</p>
          )}
          {playlists.map((p) => {
            const active = selected === p.id;
            const isCurrent = currentPlaylistId === p.id;
            return (
              <div key={p.id} role="button" onClick={() => setSelected(p.id)} style={rowStyle(active)}>
                <i className={`pi ${active ? 'pi-check-circle' : 'pi-circle'}`}
                  style={{ color: active ? 'var(--pp-btn-honey)' : 'var(--pp-text-secondary)' }} />
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="text-truncate" style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }}>{p.name}</div>
                  <small style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.playlist_items_n', { n: p.items_count ?? 0 })}</small>
                </div>
                {isCurrent && (
                  <span className="pp-status" style={{ background: 'var(--pp-success)', color: '#fff', fontSize: 11, padding: '2px 8px', borderRadius: 999, flexShrink: 0 }}>
                    {t('video_wall.current')}
                  </span>
                )}
              </div>
            );
          })}

          {/* Unassign — fall back to the wall's own bound playlist (only when assigned) */}
          {assignmentId && (
            <div role="button" onClick={() => setSelected(OWN)} style={{ ...rowStyle(selected === OWN), marginTop: 6 }}>
              <i className={`pi ${selected === OWN ? 'pi-check-circle' : 'pi-circle'}`}
                style={{ color: selected === OWN ? 'var(--pp-btn-honey)' : 'var(--pp-text-secondary)' }} />
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }}>
                  {isGroup ? t('video_wall.groups.remove_group_playlist') : t('video_wall.use_own_playlist')}
                </div>
                <small style={{ color: 'var(--pp-text-secondary)' }}>
                  {isGroup ? t('video_wall.groups.remove_group_playlist_hint') : t('video_wall.use_own_playlist_hint')}
                </small>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="d-flex justify-content-end gap-2 pt-3 mt-2" style={{ borderTop: '1px solid var(--pp-border)' }}>
        <Button label={t('video_wall.cancel')} text onClick={onHide} disabled={saving} />
        <Button label={t('video_wall.apply')} icon="pi pi-check" loading={saving}
          disabled={unchanged} onClick={apply} />
      </div>
    </Dialog>
  );
}
